import type { FC } from 'react';
import { lazy, Suspense, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';

import * as pdfjs from 'pdfjs-dist';
import { removeWatermarkServicesList } from 'helpers/services/removeWatermarkServicesList';
/** Helpers */
import { converterServicesList, editorServicesList } from 'helpers/services/servicesList';
/** Hooks */
import useClarity from 'hooks/useClarity';
import useCustomerIO from 'hooks/useCustomerIO';
/** Styles */
import { useCustomerIOAttributes } from 'hooks/useCustomerIOAttributes';
import { useDownloadDocumentWithSse } from 'hooks/useDownloadDocumentWithSse';
import { useIdentifyUser } from 'hooks/useIdentifyUser';
import useResetPassword from 'hooks/useResetPassword';
import { ContactUs } from 'pages/contact-us-page';
/** Providers */
import { ABTestProvider } from 'providers/abTests';
import GrowthBookCustomProvider from 'providers/growthBook';
import { LocaleLoadingProvider } from 'providers/localeProvider/localeLodingProvider';
import { PathLocaleProvider } from 'providers/pathLocaleProvider';
import { ProtectAuthRoutesProvider } from 'providers/protectAuthRoutesProvider';
import { RTLProvider } from 'providers/rtlProvider';
import 'styles/index.scss';

import { ConsentModeButton } from 'components/ConsentModeButton';
/** Components */
import ModalsController from 'components/modalsController';
import AppToaster from 'components/toast';

import { sseMessageSelector } from 'data/selectors/events';
/** Selectors */
import { getLoadingSelector } from 'data/selectors/loading';
import { userBalanceSelector } from 'data/selectors/user';

/** Use cases */

/** Interfaces */
import type { IService } from 'ts/interfaces/services/service';

import './styles.css';

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

/** Pages */
const NotFoundPage = lazy(() => import('pages/404'));
const PDFEditorPage = lazy(() => import('pages/editor'));
const PaymentSuccess = lazy(() => import('pages/paymentSuccess'));
const CheckoutPage = lazy(() => import('pages/checkout'));
const RedirectPage = lazy(() => import('pages/redirect'));
const ServicePage = lazy(() => import('pages/service'));
const LoginPage = lazy(() => import('pages/login-page'));
const SignUpPage = lazy(() => import('pages/sign-up-page'));
const AccountPage = lazy(() => import('pages/account'));

const MergePDFPage = lazy(() => import('pages/mergePdf'));
const SettingsPage = lazy(() => import('pages/settings'));
const MainPage = lazy(() => import('pages/main'));
const RouterContainer: FC = () => {
  const { t } = useTranslation();
  const isLoading = useSelector(getLoadingSelector('get_user'));
  const message = useSelector(sseMessageSelector);
  const { handleDownload } = useDownloadDocumentWithSse();
  const userBalance = useSelector(userBalanceSelector);

  useIdentifyUser();
  useResetPassword();
  useClarity();
  useCustomerIO();
  useCustomerIOAttributes();

  useEffect(() => {
    if (!message) return;

    if (message?.data?.id && !Number.isNaN(userBalance) && Number(userBalance) > 0) {
      handleDownload(message?.data?.id);
    }
    return;
    // eslint-disable-next-line
  }, [message]);

  return (
    <Router>
      <RTLProvider>
        <ABTestProvider>
          <LocaleLoadingProvider>
            <PathLocaleProvider>
              <Suspense>
                <Routes>
                  <Route path='app/tools'>
                    <Route path=':language?'>
                      <Route
                        path={'redirect'}
                        element={<RedirectPage />}
                      />
                      <Route
                        path={'login'}
                        element={<LoginPage />}
                      />
                      <Route
                        path={'sign-up'}
                        element={<SignUpPage />}
                      />
                      <Route
                        path={'404'}
                        element={<NotFoundPage />}
                      />

                      {!isLoading && (
                        <Route
                          path='*'
                          element={
                            <Navigate
                              to='404'
                              replace
                            />
                          }
                        />
                      )}
                      <Route
                        path={'account'}
                        element={<ProtectAuthRoutesProvider children={<AccountPage />} />}
                      />
                      <Route
                        path={'account-settings'}
                        element={<ProtectAuthRoutesProvider children={<SettingsPage />} />}
                      />

                      <Route
                        path={'account'}
                        element={<ProtectAuthRoutesProvider children={<AccountPage />} />}
                      />

                      <Route
                        path={`payment`}
                        element={<CheckoutPage />}
                      />
                      <Route
                        path={`payment-success`}
                        element={<PaymentSuccess />}
                      />

                      <Route
                        path={`editor`}
                        element={<PDFEditorPage />}
                      />
                      <Route
                        path={''}
                        element={<MainPage />}
                      />
                      <Route
                        path={`contact-us`}
                        element={<ContactUs />}
                      />

                      {/* Start converter tools list */}
                      {converterServicesList(t).map((item: IService, index) => (
                        <Route
                          key={`route-${item.path}-${index + 1}`}
                          path={`${item.path.replace('/', '')}`}
                          element={<ServicePage service={item} />}
                        />
                      ))}
                      {/* End converter tools list */}

                      {/* Start editor tools list */}
                      {editorServicesList(t).map((item: IService, index) => (
                        <Route
                          key={`route-${item.path}-${index + 1}`}
                          path={`${item.path.replace('/', '')}`}
                          element={<ServicePage service={item} />}
                        />
                      ))}
                      {/* End editor tools list */}
                      {removeWatermarkServicesList(t).map((item: IService, index: number) => (
                        <Route
                          key={`route-${item.path}-${index + 1}`}
                          path={`${item.path.replace('/', '')}`}
                          element={<ServicePage service={item} />}
                        />
                      ))}
                      <Route
                        path={`complete-merge`}
                        element={<MergePDFPage />}
                      />
                    </Route>
                  </Route>
                </Routes>
              </Suspense>
            </PathLocaleProvider>
            <ModalsController />
            <AppToaster />
            <ConsentModeButton />
          </LocaleLoadingProvider>
        </ABTestProvider>
      </RTLProvider>
    </Router>
  );
};

const App: FC = () => {
  return (
    <>
      <GrowthBookCustomProvider>
        <RouterContainer />
      </GrowthBookCustomProvider>
    </>
  );
};

export default App;
